
    import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
    import has from 'lodash/has';
    import dayjs from 'dayjs';
    import Cookie from 'js-cookie';

    import LazyHydrate from 'vue-lazy-hydration';
    import { Bus } from '~/utils/bus';
    import JobViewHeader from '~/components/JobView/JobViewHeader.vue';
    import JobViewTags from '~/components/JobView/JobViewTags.vue';
    import JobViewPayComparison from '~/components/JobView/JobViewPayComparison.vue';
    import JobViewJd from '~/components/JobView/JobViewJd.vue';
    import JobViewJobDetails from '~/components/JobView/JobViewJobDetails.vue';
    import JobViewMixin from '~/components/JobView/Mixins/JobViewMixin.vue';
    import { slugify } from '~/utils/helpers';
    import datalayer from '~/utils/datalayer';
    import { jobData } from '~/utils/jobData';
    import EducationSlideWidget from '~/components/Education/EducationSlideWidget.vue';
    import JobViewApplySticky from '~/components/JobView/JobViewApplySticky.vue';
    import JobWidget from '~/components/Utils/JobWidget.vue';
    import ResumeWriteMrec from '~/components/ResumeWriting/ResumeWriteMrec.vue';
    import ResumeWriteBottomDialogue from '~/components/ResumeWriting/ResumeWriteBottomDialogue.vue';
    import JobViewCompany from '~/components/JobView/JobViewCompany.vue';
    import JobViewBrandVisual from '@/components/JobView/JobViewBrandVisual';
    import JobViewStatusBar from '@/components/JobView/JobViewStatusBar';
    import JobViewCtaFlier from '@/components/JobView/JobViewCtaFlier';
    import AdUnit from '@/components/Ad/AdUnit';

    export default {
        name: 'JobView',

        components: {
            LazyHydrate,
            AdUnit,
            JobViewHeader,
            JobViewTags,
            JobViewJd,
            JobViewPayComparison,
            JobViewJobDetails,
            JobViewApplySticky,
            JobWidget,
            EducationSlideWidget,
            ResumeWriteMrec,
            ResumeWriteBottomDialogue,
            JobViewCompany,
            JobViewBrandVisual,
            JobViewStatusBar,
            JobViewCtaFlier,
        },

        mixins: [JobViewMixin],

        props: {
            containerClass: {
                type: String,
                default: '',
            },
            showCtaFlier: {
                type: Boolean,
                default: false,
            },
            ctaFlierTransition: {
                type: Object,
                default: () => {
                    return {
                        beforeEnter: '-100%',
                        enter: [0, '-100%'],
                        leave: ['-100%', 0],
                    };
                },
            },
        },

        data() {
            return {
                hasJustApplied: false,
                showJobTeaser: true,
            };
        },

        computed: {
            ...mapState({
                $_jobTeaser: state => state.jobTeaser,
                $_jobModal: state => state.jobModal,
                $_search: state => state.search,
                $_meAttributeOptions: state => state.me.attributeOptions,
                $_jobApplication: state => state.jobApplication,
                $_tracking: state => state.eventTracking,
                $_isApplyEnabled: state => state.job.is_apply_enabled,
                $_isDomainSessionAvailable: state => state.eventTracking.isDomainSessionAvailable,
            }),

            ...mapGetters('me', {
                $_attributeOptionsSkills: 'attributeOptionsSkills',
                $_attributeOptionsPerks: 'attributeOptionsPerks',
                $_attributeOptionsCertifications: 'attributeOptionsCertifications',
            }),

            ...mapGetters({
                $_currentAppUrl: 'currentAppUrl',
            }),

            ...mapGetters('job', {
                $_hasApplied: 'hasApplied',
            }),

            ...mapGetters('jobModal', {
                $_isJobModalOpen: 'isJobModalOpen',
            }),

            classList() {
                return [
                    'jv',
                    this.getTeaserClass,
                ];
            },

            getTeaserClass() {
                return !this.$_jobTeaser.jobs.length ? 'no-teaser' : '';
            },

            scoringSkillsVariant() {
                if (!this.scoringSkills) {
                    return null;
                }

                let output;
                const score = this.scoringSkills.score;

                if (score <= 33) {
                    output = 'danger';
                } else if (score <= 66) {
                    output = 'warning';
                } else {
                    output = 'success';
                }

                return output;
            },

            scoringSkills() {
                return this.scoring.scoreDetails.find(o => o.title === 'Skills');
            },

            scoringCertificationsVariant() {
                if (!this.scoringCertifications) {
                    return null;
                }

                let output;
                const score = this.scoringCertifications.score;

                if (score <= 33) {
                    output = 'danger';
                } else if (score <= 66) {
                    output = 'warning';
                } else {
                    output = 'success';
                }

                return output;
            },

            sectionTitle() {
                if (this.$_isJobModalOpen) {
                    return this.$_jobModal.sectionTitle;
                }

                return '';
            },

            scoringCertifications() {
                return this.scoring.scoreDetails.find(o => o.title === 'Certifications & licenses');
            },

            currentJobIndex() {
                return this.$_jobTeaser.jobs.length ? this.$_jobTeaser.jobs.findIndex(o => o.uuid === this.$_job.uuid) : -1;
            },

            previousJob() {
                const currentJobIndex = this.currentJobIndex;

                if (currentJobIndex >= 0 && currentJobIndex > 0) {
                    const job = this.$_jobTeaser.jobs[currentJobIndex - 1];
                    job.slug = slugify(job.job_title);

                    return job;
                }

                return {};
            },

            nextJob() {
                const currentJobIndex = this.currentJobIndex;

                if (currentJobIndex >= 0 && currentJobIndex < this.$_jobTeaser.jobs.length - 1) {
                    const job = this.$_jobTeaser.jobs[currentJobIndex + 1];
                    job.slug = slugify(job.job_title);

                    return job;
                }

                return {};
            },

            occupationIds() {
                if (!this.$_job.occupations_list || !Array.isArray(this.$_job.occupations_list)) {
                    return [];
                }

                return this.$_job.occupations_list.map(o => o.occupation_id);
            },
        },

        watch: {
            $_meAttributeOptions: {
                handler() {
                    this.$_syncScoring();
                },
                deep: true,
            },

            $_isDomainSessionAvailable(value) {
                if (value) {
                    this.pushAllEvents();
                }
            },
        },

        created() {
            let rwShowBottomDialogue = Cookie.get('rw_show_bottom_dialogue');
            if (typeof rwShowBottomDialogue === 'undefined') {
                rwShowBottomDialogue = 'true';
            }

            let rwJobViewCount = Cookie.get('rw_job_view_count');
            if (typeof rwShowBottomDialogue === 'undefined') {
                rwJobViewCount = 0;
            } else {
                rwJobViewCount = parseInt(Cookie.get('rw_job_view_count'));
            }
            this.$_SET_STATE_BOTTOM_DIALOGUE(rwShowBottomDialogue);
            this.$_SET_JOB_VIEW_COUNT(rwJobViewCount);
        },

        mounted() {
            this.$_INCREASE_JOB_VIEW_COUNT();

            if (this.$_me.id) {
                this.loadCustomQuestions();
            }

            if (this.$_isJobModalOpen) {
                this.pushAllEvents();
                window.dataLayer.push({
                    event: 'pageview',
                    page: {
                        path: this.$route.fullPath,
                        full_path: `${this.$siteUrl}${this.$route.fullPath.substring(1)}`,
                    },
                });
            }

            if (process.browser) {
                Bus.$on('skipToJob', () => {
                    this.skipToJob('apply_button');
                });
            }

            this.$_RESET_APPLY();
            this.$_CLOSE_APPLY_FORM();
            this.$_CLOSE_APPLY_OFFSITE_FORM();

            if (this.$_job.is_incomplete_apply) {
                this.$_STORE_APPLY_UUID(this.$_job.apply_uuid);
            }
        },

        beforeDestroy() {
            Bus.$off('skipToJob');
        },

        methods: {
            ...mapActions('me', {
                $_syncScoring: 'syncScoring',
            }),

            ...mapActions('job', {
                $_searchJobsByFilter: 'searchJobsByFilter',
            }),

            ...mapMutations('me', {
                $_PUT_JOB_EVENT: 'PUT_JOB_EVENT',
            }),

            ...mapMutations('jobApplication', {
                $_APPLY: 'APPLY',
                $_RESET_APPLY: 'RESET_APPLY',
                $_CLOSE_APPLY_FORM: 'CLOSE_APPLY_FORM',
                $_CLOSE_APPLY_OFFSITE_FORM: 'CLOSE_APPLY_OFFSITE_FORM',
                $_STORE_APPLY_UUID: 'STORE_APPLY_UUID',
                $_STORE_CUSTOM_QUESTION_STEP: 'STORE_CUSTOM_QUESTION_STEP',
                $_STORE_CUSTOM_QUESTION_LOADING: 'STORE_CUSTOM_QUESTION_LOADING',
                $_OPEN_CUSTOM_QUESTIONS: 'OPEN_CUSTOM_QUESTIONS',
                $_OPEN_APPLY_FORM: 'OPEN_APPLY_FORM',
            }),

            ...mapMutations('jobModal', {
                $_STORE_JOB_UUID: 'STORE_JOB_UUID',
            }),

            ...mapMutations('jobTeaser', {
                $_STORE_JOB_TEASER_PAGE: 'STORE_JOB_TEASER_PAGE',
                $_CONCAT_JOB_TEASER: 'CONCAT_JOB_TEASER',
            }),

            ...mapMutations('job', {
                $_UPDATE_JOB: 'UPDATE_JOB',
            }),

            ...mapMutations('resumeWrite', {
                $_INCREASE_JOB_VIEW_COUNT: 'INCREASE_JOB_VIEW_COUNT',
                $_SET_STATE_BOTTOM_DIALOGUE: 'SET_STATE_BOTTOM_DIALOGUE',
                $_SET_JOB_VIEW_COUNT: 'SET_JOB_VIEW_COUNT',
            }),

            ...mapActions('eventTracking', {
                $_pushEvent: 'pushEvent',
                $_pushSeekerJobEvent: 'pushSeekerJobEvent',
            }),

            ...mapActions('seekerEvents', {
                $_pushSeekerEvent: 'pushSeekerEvent',
            }),

            ...mapActions('jobApplication', {
                $_getJobQuestions: 'getJobQuestions',
            }),

            pushAllEvents() {
                const dataLayer = {
                    event: 'job_view',
                    ...this.$_jobData,
                    seeker_match_score: this.scoring,
                    seeker_data: this.$_scoringData,
                };

                this.$_PUT_JOB_EVENT({ job: this.$_job.uuid, status: 'is_viewed' });
                this.$_pushEvent(dataLayer);
                this.$_pushSeekerJobEvent(dataLayer);
                if (this.$_me.id) {
                    this.$_pushSeekerEvent({
                        event_code: 'JOB_VIEW',
                        job_uuid: this.$_job.uuid,
                    });
                }
                datalayer(dataLayer);
            },

            skipToJob(source) {
                const url = `${this.$siteUrl}job-redirect/${this.$_job.uuid}?flow=${this.$_job.flow_enabled === 'FLOW'}&token=${this.$_job.token}&source=${source}&page_referral_element=${this.$_tracking.page_referral_element}`;
                window.open(url, '_blank');

                this.$_APPLY();
                this.$_UPDATE_JOB({ key: 'has_applied', value: true });
                this.$_UPDATE_JOB({ key: 'applied_at', value: dayjs().format('YYYY-MM-DD HH:mm:ss') });
                this.$_PUT_JOB_EVENT({ job: this.$_job.uuid, status: 'is_applied' });

                datalayer({
                    event: 'apply_click_intercept',
                    intercept_type: 'flow',
                });
            },

            jobClickEvent(job, direction) {
                const data = {
                    event: 'job_click',
                    site_position: direction,
                    site_position_label: direction,
                    seeker_match_score: this.scoring,
                    seeker_data: this.$_scoringData,
                    ...jobData(job),
                };

                this.$_pushEvent(data);
                this.$_pushSeekerJobEvent(data);
                datalayer(data);
            },

            jobTeaserBeforeEnter(el) {
                el.style.translateY = '100%';
            },

            jobTeaserEnter(el, done) {
                this.$velocity(el, { translateY: [0, '100%'] }, {
                    easing: 'easeInOutQuart',
                    duration: 600,
                    complete: done,
                });
            },

            jobTeaserLeave(el, done) {
                this.$velocity(el, { translateY: ['100%', 0] }, {
                    easing: 'easeInOutQuart',
                    duration: 400,
                    complete: done,
                });
            },

            loadCustomQuestions() {
                if (has(this.$route.query, 'custom_question_step') && this.$_job.is_incomplete_apply) {
                    this.$_STORE_APPLY_UUID(this.$_job.apply_uuid);
                    this.$_STORE_CUSTOM_QUESTION_STEP(this.$route.query.custom_question_step);

                    this.$_STORE_CUSTOM_QUESTION_LOADING(true);
                    this.$_getJobQuestions({ jobUuid: this.$_job.uuid, applicationUuId: this.$_jobApplication.apply_uuid }).then(() => {
                        if (this.$_jobApplication.questions.length) {
                            this.$_OPEN_APPLY_FORM();
                            this.$_OPEN_CUSTOM_QUESTIONS();
                        }
                    }).finally(() => {
                        this.$_STORE_CUSTOM_QUESTION_LOADING(false);
                    });
                }
            },

            ctaFlierBeforeEnter(el) {
                el.style.translateY = this.ctaFlierTransition.beforeEnter;
            },

            ctaFlierEnter(el, done) {
                this.$velocity(el, { translateY: this.ctaFlierTransition.enter }, { easing: 'easeInOutQuart', duration: 900, complete: done });
            },

            ctaFlierLeave(el, done) {
                this.$velocity(el, { translateY: this.ctaFlierTransition.leave }, { easing: 'easeInOutQuart', duration: 600, complete: done });
            },
            canShowAD() {
                if (this.$_job.bucket_code === 'ORGANIC') {
                    return true;
                }
                return false;
            },
        },
    };
